import { routes } from "../../assets/constants";
import { ApiMethod } from "../models/api-types.type";
import { useAuthContext } from "./use-auth-context.hook";

/** A YYYY-MM-DD range; both ends inclusive. */
export interface AdsRange {
    startDate: string;
    endDate: string;
}

export type AdProvider = 'meta' | 'google';

/** What our own site recorded from a campaign's visitors — never the platform's claim. */
export interface AdOutcomes {
    visitors: number;
    engagements: number;
    roomUploads: number;
    chats: number;
    cartClicks: number;
    leads: number;
}

export interface AdCampaignReport {
    provider: AdProvider;
    externalId: string;
    name: string;
    status: 'active' | 'paused' | 'ended' | 'draft';
    dailyBudget?: number;
    currency?: string;
    utmCampaign?: string;
    lastSyncAt?: string;
    spend: number;
    impressions: number;
    clicks: number;
    ctr: number | null;
    cpc: number | null;
    providerConversions: number;
    providerRevenue: number;
    providerRoas: number | null;
    ours: AdOutcomes;
    costPerLead: number | null;
    costPerEngagement: number | null;
    shareOfSpend: number | null;
    activeDays: number;
}

export interface AdPacing {
    month: string;
    budget: number | null;
    spend: number;
    expected: number | null;
    pace: number | null;
    projected: number | null;
    remaining?: number;
    currency: string | null;
}

export interface AdsOverview {
    range: AdsRange;
    totals: {
        spend: number;
        impressions: number;
        clicks: number;
        ctr: number | null;
        cpc: number | null;
        providerConversions: number;
        providerRevenue: number;
        providerRoas: number | null;
        leads: number;
        costPerLead: number | null;
        engagements: number;
        costPerEngagement: number | null;
        roomUploads: number;
        chats: number;
        activeCampaigns: number;
        currency: string | null;
    };
    previous?: { spend: number; leads: number; costPerLead: number | null };
    series: Array<{ date: string; spend: number; clicks: number; leads: number }>;
    byProvider: Array<{ provider: AdProvider; spend: number; leads: number; costPerLead: number | null; share: number | null }>;
    topCampaigns: AdCampaignReport[];
    unmatchedCampaigns: Array<{ campaign: string; visitors: number; leads: number; engagements: number }>;
    pacing: AdPacing;
}

export interface AdConnection {
    uuid: string;
    provider: AdProvider;
    externalAccountId: string;
    accountName: string;
    currency: string | null;
    timezone: string | null;
    status: 'connected' | 'needs_reauth' | 'disconnected';
    lastSyncAt: string | null;
    lastError: string | null;
    connectedByEmail: string | null;
    tokenExpiresAt: string | null;
    createdAt: string | null;
}

export interface AdConnections {
    connections: AdConnection[];
    providers: Array<{ provider: AdProvider; configured: boolean }>;
}

export type AudienceSource = 'leads' | 'visualized_not_quoted' | 'asked_not_quoted' | 'product_interest' | 'all_engaged';

export interface AdAudience {
    uuid: string;
    name: string;
    source: AudienceSource;
    windowDays: number;
    products: string[];
    provider: AdProvider | null;
    connectionUuid: string | null;
    status: 'draft' | 'syncing' | 'synced' | 'failed';
    matchedCount: number | null;
    providerSize: number | null;
    lastSyncedAt: string | null;
    lastError: string | null;
    createdAt: string;
}

export interface AudiencePreview {
    source: AudienceSource;
    windowDays: number;
    size: number;
    usable: boolean;
    minimum: number;
}

export type AdSuggestionKind =
    | 'shift_budget' | 'set_budget' | 'pause_campaign' | 'create_audience'
    | 'promote_product' | 'cover_demand' | 'refresh_creative' | 'budget_pacing';

export interface AdSuggestion {
    uuid: string;
    key: string;
    kind: AdSuggestionKind;
    provider: AdProvider | null;
    title: string;
    detail: string;
    evidence: Record<string, any>;
    estimatedMonthlyImpact: number | null;
    currency: string | null;
    severity: 'critical' | 'warning' | 'info';
    status: 'open' | 'applied' | 'dismissed' | 'failed';
    applicable: boolean;
    appliedAt: string | null;
    resultNote: string | null;
    createdAt: string | null;
}

export interface AdBudgets {
    currentMonth: string;
    budgets: Array<{ month: string; provider: AdProvider | null; amount: number; currency: string | null; note: string | null; spend: number }>;
    spendByMonth: Record<string, number>;
}

export interface SyncResult {
    connectionUuid: string;
    provider: AdProvider;
    accountName: string;
    ok: boolean;
    campaigns?: number;
    days?: number;
    error?: string;
}

const query = (params: Record<string, string | number | boolean | undefined>) => {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== '') search.set(key, String(value));
    });
    const text = search.toString();
    return text ? `?${text}` : '';
};

/** Everything the Ads pages read and change. */
export const useAdsApi = () => {
    const { sendAuthGuardedRequest } = useAuthContext();
    const send = <T,>(method: ApiMethod, path: string, body?: unknown) =>
        sendAuthGuardedRequest(method, path, body) as Promise<T>;

    return {
        getOverview: (range: AdsRange) => send<AdsOverview>(ApiMethod.GET, routes.ads.overview + query({ ...range })),
        getCampaigns: (range: AdsRange) =>
            send<{ range: AdsRange; campaigns: AdCampaignReport[] }>(ApiMethod.GET, routes.ads.campaigns + query({ ...range })),
        getCampaign: (provider: AdProvider, externalId: string, range: AdsRange) =>
            send<{ range: AdsRange; campaign: AdCampaignReport | null; series: Array<{ date: string; spend: number; clicks: number; impressions: number }> }>(
                ApiMethod.GET,
                routes.ads.campaign(provider, externalId) + query({ ...range }),
            ),
        setCampaignStatus: (provider: AdProvider, externalId: string, status: 'active' | 'paused') =>
            send(ApiMethod.POST, routes.ads.campaignStatus(provider, externalId), { status }),
        setCampaignBudget: (provider: AdProvider, externalId: string, dailyBudget: number) =>
            send(ApiMethod.POST, routes.ads.campaignBudget(provider, externalId), { dailyBudget }),

        getBudgets: () => send<AdBudgets>(ApiMethod.GET, routes.ads.budgets),
        setBudget: (input: { month: string; provider?: AdProvider; amount: number; currency?: string; note?: string }) =>
            send<AdBudgets>(ApiMethod.POST, routes.ads.budgets, input),

        getConnections: () => send<AdConnections>(ApiMethod.GET, routes.ads.connections),
        getPendingConnections: () => send<AdConnection[]>(ApiMethod.GET, routes.ads.pendingConnections),
        authorize: (provider: AdProvider) => send<{ url: string }>(ApiMethod.POST, routes.ads.authorize(provider)),
        selectConnections: (uuids: string[]) => send<AdConnections>(ApiMethod.POST, routes.ads.selectConnections, { uuids }),
        disconnect: (uuid: string) => send(ApiMethod.DELETE, routes.ads.connection(uuid)),
        sync: (connectionUuid?: string) =>
            send<{ accounts?: number; results?: SyncResult[] } & Partial<SyncResult>>(ApiMethod.POST, routes.ads.sync, { connectionUuid }),

        getAudiences: () => send<AdAudience[]>(ApiMethod.GET, routes.ads.audiences),
        previewAudiences: (windowDays = 90) => send<AudiencePreview[]>(ApiMethod.GET, routes.ads.audiencePreview + query({ windowDays })),
        createAudience: (input: { name: string; source: AudienceSource; windowDays?: number }) =>
            send<AdAudience>(ApiMethod.POST, routes.ads.audiences, input),
        pushAudience: (uuid: string, connectionUuid: string) =>
            send<{ sent: number; note: string }>(ApiMethod.POST, routes.ads.audiencePush(uuid), { connectionUuid }),
        removeAudience: (uuid: string) => send(ApiMethod.DELETE, routes.ads.audience(uuid)),

        getSuggestions: (status: 'open' | 'applied' | 'dismissed' | 'all' = 'open') =>
            send<AdSuggestion[]>(ApiMethod.GET, routes.ads.suggestions + query({ status })),
        refreshSuggestions: () => send<{ found: number; suggestions: AdSuggestion[] }>(ApiMethod.POST, routes.ads.refreshSuggestions),
        applySuggestion: (uuid: string) => send<{ note: string }>(ApiMethod.POST, routes.ads.applySuggestion(uuid)),
        dismissSuggestion: (uuid: string) => send(ApiMethod.POST, routes.ads.dismissSuggestion(uuid)),
    };
};
